/**
 * Back-derive an opaque base-remainder (uplifted, materials) from a stored
 * (already-integrated) price and a fixed uplift-EXEMPT component (labor/
 * chassis, spec §Task16): base = (price - exempt)/(1+int%), refined until
 * the forward rollup reproduces the stored cent price EXACTLY (spec §7.2
 * round-trip identity) — mirrors engine.ts's priceSku ONE rounding point:
 * HALF_EVEN((uplifted*(1e4+int) + exempt*1e4) / 1e8). µ$ granularity
 * (1e4 µ$/cent step ≈ 1.08e-4 cents) guarantees a solution.
 */
import { mulDivHalfEven, dotDivHalfEven, assertSafeInt } from './money';

/** HALF_EVEN((a*b - c*d) / den) — delegates to money.ts's dotDivHalfEven, the
 * single home of this fused two-term-numerator shape (part-3 consolidation;
 * solve.ts's combineDivideHalfEven copy was removed the same way). dotDivHalfEven
 * can reject this either pre-division (numerator too large relative to
 * MAX_SAFE_INTEGER·den) or post-division (unsafe quotient) — both are
 * re-wrapped under this call site's own label ('estimateBaseMicros result')
 * because a golden test (backDerive.test.ts) pins that exact message prefix. */
function estimateBaseMicros(targetDiscountCents: number, integrationBp: number,
                            exemptMicros: number): number {
  assertSafeInt(targetDiscountCents, 'targetDiscountCents');
  assertSafeInt(integrationBp, 'integrationBp');
  assertSafeInt(exemptMicros, 'exemptMicros');
  const den = 10000 + integrationBp;
  if (den <= 0) throw new Error(`integrationBp too negative: ${integrationBp}`);
  try {
    return dotDivHalfEven([[targetDiscountCents, 100000000], [exemptMicros, -10000]], den);
  } catch (e) {
    if (e instanceof Error && /unsafe/i.test(e.message)) {
      throw new Error(`estimateBaseMicros result must be a safe integer (${e.message})`);
    }
    throw e;
  }
}

/** Thrown ONLY when the uplift-exempt components alone already exceed the
 *  target price (`forward(0) > target`) — the sole throw site in this module
 *  that is a genuine BUSINESS infeasibility rather than an internal
 *  precondition violation (an unsafe-integer guard, a non-positive
 *  denominator, or the walk/scan loops failing to converge — all of which
 *  are bugs, not pricing decisions). A caller reporting "the components
 *  cannot fit under this price" to a reviewer should catch THIS type
 *  specifically and let every other failure propagate; matching on message
 *  text would be fragile, and catching every `Error` here would mislabel a
 *  solver bug as a feasibility finding. */
export class InfeasibleBaseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InfeasibleBaseError';
  }
}

export function solveBaseMicros(
  targetDiscountCents: number, integrationBp: number, exemptMicros: number,
): number {
  const forward = (b: number) =>
    mulDivHalfEven(b * (10000 + integrationBp) + exemptMicros * 10000, 1, 100000000);

  // forward is monotonic non-decreasing in b, so forward(0) > target is the
  // exact infeasibility condition (unlike the crude point-estimate below).
  if (forward(0) > targetDiscountCents) throw new InfeasibleBaseError(
    `exempt components (${exemptMicros} µ$) exceed target ${targetDiscountCents}¢ at ${integrationBp}bp`);

  // Initial estimate (algebraic, ignoring rounding): b ≈ (target*1e8 -
  // exempt*1e4)/(1e4+int) — exact via BigInt (estimateBaseMicros), then walk
  // ±1 µ$-quantum steps until exact. May be off by ±1 µ$ from the true
  // real-valued inverse (rounding); the walk loop below corrects that.
  let b = Math.max(0, estimateBaseMicros(targetDiscountCents, integrationBp, exemptMicros));

  // one cent of output ≈ 1e8/(1e4+bp) µ$ of input; scan a small window
  const step = Math.max(1, Math.floor(100000000 / (10000 + integrationBp) / 4));
  for (let i = 0; i < 100000; i++) {
    const got = forward(b);
    if (got === targetDiscountCents) return b;
    b += got < targetDiscountCents ? step : -step;
    if (b < 0) b = 0;
    // Once within 1 cent of the target, step (which never shrinks) may
    // overshoot past the exact value; solveExact scans a ±30000 µ$ window
    // (≈±3.2¢) linearly to find it.
    if (Math.abs(got - targetDiscountCents) <= 1 && step > 1) {
      return solveExact(b, targetDiscountCents, forward);
    }
  }
  throw new Error(`solveBaseMicros failed to converge on ${targetDiscountCents}¢`);
}

function solveExact(start: number, target: number,
                    forward: (b: number) => number): number {
  for (let b = Math.max(0, start - 30000); b <= start + 30000; b++) {
    if (forward(b) === target) return b;
  }
  throw new Error(`no exact base found near ${start} for ${target}¢`);
}
